// Percorso file: src/components/games/regolePartita.js
//
// LE REGOLE DI GIOCO DI GAMES — cantiere GAMES, fetta G2 (il cuore),
// PIANO-GAMES §3 e §7 dell'incarico. FUNZIONI PURE, SENZA REACT E SENZA
// IMPORT: prendono lo stato della partita e restituiscono lo stato nuovo,
// mai mutato. Vivono in un modulo a sé per una ragione sola: si provano in
// un banco Node su dati inventati (docs/cantieri/games/banco/), come le
// funzioni pure di server.js (CLAUDE.md: «una regola non si deduce, si
// prova»). Tutto ciò che qui è deciso viene dal PIANO; ciò che il PIANO non
// dice è dichiarato nel referto G2.
//
// IL PRINCIPIO CHE TIENE TUTTO (PIANO §1): il minutaggio NON si legge
// dall'orologio, si calcola da CHI È IN CAMPO E PER QUANTO. Il cronometro è
// manuale — lo avvia, lo ferma e lo fa correre il coach — e i minuti di un
// giocatore sono la somma dei suoi intervalli in campo, misurati sul tempo
// DI GARA (i due tempi si sommano).
//
// LA FORMA DELLO STATO — `partita`:
//
//   cron       il cronometro: { tempo: 1|2, inCorso, avviatoA, accumulato,
//              precedenti, finita }
//                · `accumulato`  secondi del TEMPO CORRENTE già contati (in
//                                pausa è tutto qui);
//                · `avviatoA`    istante (ms) dell'ultimo avvio, mentre corre;
//                · `precedenti`  secondi dei tempi già CHIUSI (0 nel 1º tempo);
//                · `finita`      vero dopo la chiusura del 2º tempo.
//   campo      { [playerId]: { x, y } } — chi è IN CAMPO, con la posizione
//              SOLO VISIVA (frazioni 0..1 del rettangolo): non si registra.
//   minuti     { [playerId]: { accumulato, entratoA } } — secondi in campo
//              già chiusi e, se è dentro, il secondo DI GARA in cui è entrato.
//   espulsi    [playerId] — chi ha preso il rosso: fuori, non rientra.
//   eventi     [{ n, playerId, tipo, tempo, minuto, automatico }] — la
//              cronologia dei gol/gialli/rossi, col minuto messo dall'app.
//
// ⚠️ «ADESSO» È SEMPRE UN PARAMETRO (ms, `Date.now()` nel browser): le
// funzioni non leggono l'orologio da sole, così il banco le prova con un
// tempo inventato e deterministico. Il cronometro misura il tempo TRASCORSO
// fra avvio e pausa (come un cronometro da polso): non è legato all'ora
// della partita e sopravvive al telefono che spegne lo schermo, perché
// alla ripresa il conto si rifà da `avviatoA` e non da un contatore a
// scatti.

export const TIPI_EVENTO = Object.freeze({
  GOL: "goal",
  GIALLO: "yellow",
  ROSSO: "red"
});

/** La posizione predefinita di chi entra senza un punto scelto: centro. */
export const CENTRO_CAMPO = Object.freeze({ x: 0.5, y: 0.5 });

const chiave = (id) => String(id);

const secondiDa = (adesso, avviatoA) => Math.max(0, (adesso - avviatoA) / 1000);

// ===========================================================================
// IL CRONOMETRO — manuale, due tempi che ripartono da zero (PIANO §3)
// ===========================================================================

export function cronometroIniziale() {
  return {
    tempo: 1,
    inCorso: false,
    avviatoA: null,
    accumulato: 0,
    precedenti: 0,
    finita: false
  };
}

/** I secondi del tempo corrente, correnti se il cronometro corre. */
export function secondiDelTempo(cron, adesso) {
  return cron.accumulato + (cron.inCorso ? secondiDa(adesso, cron.avviatoA) : 0);
}

/** I secondi DI GARA: i tempi chiusi più il tempo corrente. */
export function secondiDiGara(cron, adesso) {
  return cron.precedenti + secondiDelTempo(cron, adesso);
}

/** Il minuto che l'app scrive su un evento: 0:00-0:59 è il 1', come nel
 *  calcio. Riparte da 1 a ogni tempo (i tempi non hanno durata dichiarata,
 *  quindi il 2º tempo non parte da 46): l'evento porta anche il tempo. */
export function minutoCorrente(cron, adesso) {
  return Math.floor(secondiDelTempo(cron, adesso) / 60) + 1;
}

export function avviaCronometro(cron, adesso) {
  if (cron.inCorso || cron.finita) return cron;
  return { ...cron, inCorso: true, avviatoA: adesso };
}

/** In pausa il tempo si congela: ciò che è corso finisce in `accumulato`,
 *  e da qui in poi nessuno in campo accumula minuti (PIANO §3). */
export function pausaCronometro(cron, adesso) {
  if (!cron.inCorso) return cron;
  return {
    ...cron,
    inCorso: false,
    avviatoA: null,
    accumulato: secondiDelTempo(cron, adesso)
  };
}

/** L'avanzamento veloce (per simulare): aggiunge secondi al tempo corrente.
 *  SOLO mentre il cronometro corre — in pausa nessuno accumula, e far
 *  correre il tempo da fermi contraddirebbe la pausa. */
export function avanzaCronometro(cron, secondi) {
  if (!cron.inCorso || cron.finita) return cron;
  const passo = Math.max(0, Number(secondi) || 0);
  return { ...cron, accumulato: cron.accumulato + passo };
}

/** Chiude il tempo corrente: 1º tempo → intervallo col cronometro fermo a
 *  zero del 2º; 2º tempo → partita finita. I secondi del tempo chiuso vanno
 *  in `precedenti`, così il tempo DI GARA continua a sommare. */
export function chiudiTempo(cron, adesso) {
  const fermo = pausaCronometro(cron, adesso);
  const precedenti = fermo.precedenti + fermo.accumulato;

  if (fermo.tempo === 1) {
    return { ...fermo, tempo: 2, accumulato: 0, precedenti };
  }
  return { ...fermo, accumulato: 0, precedenti, finita: true };
}

/** «mm:ss» del tempo corrente, per il quadrante. */
export function quadrante(secondi) {
  const tot = Math.max(0, Math.floor(secondi));
  const m = Math.floor(tot / 60);
  const s = tot % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

// ===========================================================================
// LA PARTITA — campo, minuti, eventi
// ===========================================================================

/** Lo stato di partenza: tutti i convocati in panchina, campo vuoto. */
export function nuovaPartita(convocati) {
  const minuti = {};
  for (const id of convocati) {
    minuti[chiave(id)] = { accumulato: 0, entratoA: null };
  }
  return {
    cron: cronometroIniziale(),
    campo: {},
    minuti,
    espulsi: [],
    eventi: []
  };
}

export const eInCampo = (partita, id) =>
  Object.prototype.hasOwnProperty.call(partita.campo, chiave(id));

export const eEspulso = (partita, id) => partita.espulsi.includes(chiave(id));

export const eConvocato = (partita, id) =>
  Object.prototype.hasOwnProperty.call(partita.minuti, chiave(id));

/** In panchina = convocato, non in campo, non espulso. */
export const eInPanchina = (partita, id) =>
  eConvocato(partita, id) && !eInCampo(partita, id) && !eEspulso(partita, id);

/** I secondi in campo di un giocatore, fin qui: gli intervalli chiusi più
 *  quello aperto (se è dentro), misurato sul tempo DI GARA. */
export function secondiInCampo(partita, id, adesso) {
  const m = partita.minuti[chiave(id)];
  if (!m) return 0;
  const aperto =
    m.entratoA == null ? 0 : Math.max(0, secondiDiGara(partita.cron, adesso) - m.entratoA);
  return m.accumulato + aperto;
}

/** I minuti giocati, arrotondati al minuto più vicino: chi ha giocato tutto
 *  un tempo chiuso a 39:59 ha giocato 40', non 39. */
export function minutiGiocati(partita, id, adesso) {
  return Math.round(secondiInCampo(partita, id, adesso) / 60);
}

const posizioneValida = (pos) =>
  pos && Number.isFinite(pos.x) && Number.isFinite(pos.y)
    ? { x: Math.min(1, Math.max(0, pos.x)), y: Math.min(1, Math.max(0, pos.y)) }
    : { ...CENTRO_CAMPO };

/** Si sposta dentro il campo: SOLO visivo, non è un cambio (PIANO §3). */
export function spostaInCampo(partita, id, pos) {
  const k = chiave(id);
  if (!eInCampo(partita, k)) return partita;
  return { ...partita, campo: { ...partita.campo, [k]: posizioneValida(pos) } };
}

/** Entra in campo (o, se è già dentro, si sposta): apre l'intervallo al
 *  secondo di gara corrente. Un espulso o un non convocato non entra. */
export function entraInCampo(partita, id, pos, adesso) {
  const k = chiave(id);
  if (!eConvocato(partita, k) || eEspulso(partita, k)) return partita;
  if (eInCampo(partita, k)) return spostaInCampo(partita, k, pos);

  return {
    ...partita,
    campo: { ...partita.campo, [k]: posizioneValida(pos) },
    minuti: {
      ...partita.minuti,
      [k]: { ...partita.minuti[k], entratoA: secondiDiGara(partita.cron, adesso) }
    }
  };
}

/** Esce dal campo: chiude l'intervallo, i secondi finiscono in `accumulato`.
 *  Nessun rimpiazzo richiesto (PIANO §3). */
export function esceDalCampo(partita, id, adesso) {
  const k = chiave(id);
  if (!eInCampo(partita, k)) return partita;

  const campo = { ...partita.campo };
  delete campo[k];

  return {
    ...partita,
    campo,
    minuti: {
      ...partita.minuti,
      [k]: { accumulato: secondiInCampo(partita, k, adesso), entratoA: null }
    }
  };
}

/** Il gesto «lasciato SOPRA un altro» (PIANO §3), a quattro esiti:
 *    panchina → campo   : il trascinato entra AL POSTO dell'altro, che esce;
 *    campo → panchina   : il trascinato esce e l'altro entra al suo posto;
 *    campo → campo      : si scambiano le posizioni (solo visivo);
 *    panchina → panchina: nulla. */
export function scambia(partita, idTrascinato, idSotto, adesso) {
  const a = chiave(idTrascinato);
  const b = chiave(idSotto);
  if (a === b || !eConvocato(partita, a) || !eConvocato(partita, b)) return partita;
  if (eEspulso(partita, a) || eEspulso(partita, b)) return partita;

  const aDentro = eInCampo(partita, a);
  const bDentro = eInCampo(partita, b);

  if (aDentro && bDentro) {
    return {
      ...partita,
      campo: { ...partita.campo, [a]: partita.campo[b], [b]: partita.campo[a] }
    };
  }
  if (!aDentro && bDentro) {
    const posto = partita.campo[b];
    return entraInCampo(esceDalCampo(partita, b, adesso), a, posto, adesso);
  }
  if (aDentro && !bDentro) {
    const posto = partita.campo[a];
    return entraInCampo(esceDalCampo(partita, a, adesso), b, posto, adesso);
  }
  return partita;
}

/** I gialli già presi da un giocatore (per il doppio giallo). */
export const gialliDi = (partita, id) =>
  partita.eventi.filter((e) => e.playerId === chiave(id) && e.tipo === TIPI_EVENTO.GIALLO)
    .length;

/** Vero se il prossimo giallo di questo giocatore sarebbe il secondo. */
export const prossimoGialloERosso = (partita, id) => gialliDi(partita, id) >= 1;

/** Registra un evento sul giocatore, col minuto letto dal cronometro.
 *    gol, giallo → sola statistica, il giocatore resta e i minuti corrono;
 *    rosso       → esce, non rientra, minuti fermi da quel minuto;
 *    2º giallo   → rosso AUTOMATICO, stesso effetto; in statistica conta
 *                  2 gialli + 1 rosso (PIANO §3).
 *  Un espulso non riceve altri eventi. */
export function registraEvento(partita, id, tipo, adesso) {
  const k = chiave(id);
  if (!eConvocato(partita, k) || eEspulso(partita, k)) return partita;
  if (!Object.values(TIPI_EVENTO).includes(tipo)) return partita;

  const base = {
    tempo: partita.cron.tempo,
    minuto: minutoCorrente(partita.cron, adesso)
  };
  const n = partita.eventi.length + 1;
  let eventi = [...partita.eventi, { n, playerId: k, tipo, ...base, automatico: false }];
  let dopo = { ...partita, eventi };

  let rosso = tipo === TIPI_EVENTO.ROSSO;

  if (tipo === TIPI_EVENTO.GIALLO && gialliDi(dopo, k) >= 2) {
    eventi = [
      ...eventi,
      { n: n + 1, playerId: k, tipo: TIPI_EVENTO.ROSSO, ...base, automatico: true }
    ];
    dopo = { ...dopo, eventi };
    rosso = true;
  }

  if (rosso) {
    dopo = esceDalCampo(dopo, k, adesso);
    dopo = { ...dopo, espulsi: [...dopo.espulsi, k] };
  }
  return dopo;
}

/** (G3) «CLEAN»: azzera TUTTI gli eventi di un giocatore (gol, gialli,
 *  rosso → 0) per correggere un inserimento sbagliato, e se era espulso lo
 *  toglie dagli espulsi: torna disponibile (in panchina) e può rientrare
 *  col drag & drop. I minuti NON si toccano: restano quelli calcolati da
 *  chi è in campo (l'uscita del rosso ha chiuso l'intervallo; se rientra,
 *  riprende da lì). Gli eventi degli altri restano, rinumerati per tenere
 *  `n` progressivo. Solo in memoria: nessun disco, nessuna rete. */
export function azzeraEventiGiocatore(partita, id) {
  const k = chiave(id);
  if (!eConvocato(partita, k)) return partita;

  const eventi = partita.eventi
    .filter((e) => e.playerId !== k)
    .map((e, i) => ({ ...e, n: i + 1 }));
  const espulsi = partita.espulsi.filter((x) => x !== k);

  if (eventi.length === partita.eventi.length && espulsi.length === partita.espulsi.length) {
    return partita;
  }
  return { ...partita, eventi, espulsi };
}

/** Fine partita: cronometro fermo, ogni intervallo chiuso. Da qui i minuti
 *  non cambiano più: la tabella è l'ultima parola (Strada A). Il cronometro
 *  viene marcato `finita` qualunque sia il tempo in corso (una partita si
 *  può chiudere anche dal 1º tempo, se sospesa). */
export function chiudiPartita(partita, adesso) {
  let dopo = partita;
  for (const id of Object.keys(dopo.campo)) {
    dopo = esceDalCampo(dopo, id, adesso);
  }
  if (dopo.cron.finita) return dopo;

  const fermo = pausaCronometro(dopo.cron, adesso);
  return {
    ...dopo,
    cron: {
      ...fermo,
      precedenti: fermo.precedenti + fermo.accumulato,
      accumulato: 0,
      finita: true
    }
  };
}

/** I totali per convocato, nell'ordine dato: la riga della tabella finale.
 *  Il doppio giallo esce come 2 gialli + 1 rosso, per costruzione. */
export function statistiche(partita, convocati, adesso) {
  return convocati.map((id) => {
    const k = chiave(id);
    const miei = partita.eventi.filter((e) => e.playerId === k);
    return {
      playerId: k,
      minutes: minutiGiocati(partita, k, adesso),
      goals: miei.filter((e) => e.tipo === TIPI_EVENTO.GOL).length,
      yellowCards: miei.filter((e) => e.tipo === TIPI_EVENTO.GIALLO).length,
      redCard: miei.some((e) => e.tipo === TIPI_EVENTO.ROSSO)
    };
  });
}

// ===========================================================================
// IL SALVATAGGIO — il corpo di POST /games (F1), costruito e validato qui
// ===========================================================================

/** Un intero ≥ 0 da ciò che la tabella porta (numero o testo digitato):
 *  `null` se non lo è. Il vuoto vale 0: una casella lasciata bianca è uno
 *  zero, non un errore. */
export function interoNonNegativo(valore) {
  if (valore === "" || valore == null) return 0;
  const testo = String(valore).trim();
  if (!/^\d+$/.test(testo)) return null;
  return Number(testo);
}

/** Il corpo di `POST /games`: { eventId, score: { us, them }, players: [...] }
 *  — SOLO i convocati; team/date/season li mette il backend (non si
 *  inviano). Torna { ok, corpo } oppure { ok: false, errore } con una frase
 *  per il coach. I numeri sono interi ≥ 0 (F1 respinge le stringhe). */
export function corpoSalvataggio(eventId, score, righe) {
  // Il risultato si SCRIVE A MANO (PIANO §3): due caselle vuote non sono
  // uno 0-0, sono un risultato non scritto — e non si salva.
  const cePunteggio = Boolean(score) && typeof score === "object";
  const vuoto = (v) => v == null || String(v).trim() === "";
  if (!cePunteggio || vuoto(score.us) || vuoto(score.them)) {
    return { ok: false, errore: "Enter the final score before saving." };
  }
  const us = interoNonNegativo(score.us);
  const them = interoNonNegativo(score.them);
  if (us === null || them === null) {
    return { ok: false, errore: "The score must be two whole numbers (0 or more)." };
  }

  const players = [];
  const visti = new Set();
  for (const r of righe || []) {
    const playerId = chiave(r.playerId);
    if (visti.has(playerId)) continue;
    visti.add(playerId);

    const minutes = interoNonNegativo(r.minutes);
    const goals = interoNonNegativo(r.goals);
    const yellowCards = interoNonNegativo(r.yellowCards);
    if (minutes === null || goals === null || yellowCards === null) {
      return {
        ok: false,
        errore: "Minutes, goals and yellow cards must be whole numbers (0 or more)."
      };
    }
    players.push({ playerId, minutes, goals, yellowCards, redCard: Boolean(r.redCard) });
  }

  return {
    ok: true,
    corpo: { eventId: chiave(eventId), score: { us, them }, players }
  };
}

/** La somma dei gol assegnati ai giocatori: si mostra accanto al risultato
 *  scritto a mano, SENZA forzare la quadratura (PIANO §3, §7). */
export const golAssegnati = (righe) =>
  (righe || []).reduce((tot, r) => tot + (interoNonNegativo(r.goals) || 0), 0);
